import { EmploymentStatus, VgEmploymentState } from '@interfaces';
import { CostBasisMethods, TransactionTypesEnum } from '@shared/models';
import { CostBasisState } from '@store/reducers/cost-basis/cost-basis.reducer';
import { InternalTransferState } from '@store/reducers/internal-transfer/internal-transfer.reducer';

export const getInternalTransferOverviewNextPage = (itMultiReceivingToggle: boolean): string =>
  itMultiReceivingToggle ? 'internal-transfer/multi-triage' : 'internal-transfer/triage';

export const getInternalTransferTriageNextPage = (): string => 'internal-transfer/summary';

export const getInternalTransferTriageMultiNextPage = (): string =>
  'internal-transfer/multi-summary';

const shouldShowCostBasisPages = (
  internalTransfer: InternalTransferState,
  costBasis: CostBasisState,
): boolean => {
  const isAdditionalSignerFlow: boolean = internalTransfer.transferId !== null;

  if (isAdditionalSignerFlow) {
    return false;
  }
  const eligibileCostBasisMethods = internalTransfer.transferFrom.selectedAccount?.accountData
    .managed
    ? [CostBasisMethods.AVERAGE_COST, CostBasisMethods.MIN_TAX]
    : [CostBasisMethods.AVERAGE_COST];
  if (
    costBasis.selectedPositions?.some((holding) =>
      eligibileCostBasisMethods.includes(holding.selectedCostBasisMethod || CostBasisMethods.NONE),
    )
  ) {
    return true;
  }
  return false;
};

export const getInternalTransferFromNextPage = (
  internalTransfer: InternalTransferState,
  costBasis: CostBasisState,
): string => {
  if (shouldShowCostBasisPages(internalTransfer, costBasis)) {
    return 'secure/internal-transfer/cost-basis-election';
  }
  return 'secure/internal-transfer/holdings-review';
};

export const getInternalTransferDetailsNextPage = getInternalTransferFromNextPage;

export const getInternalTransferHoldingsReviewNextPage = (
  internalTransferState: InternalTransferState,
): string => {
  const isAdditionalSignerFlow: boolean = internalTransferState.transferId !== null;
  const routes = {
    reviewPage: `secure/internal-transfer/review-multisigner`,
    personalInfo: 'secure/internal-transfer/personal-info',
    review: 'secure/internal-transfer/review',
  };

  if (isAdditionalSignerFlow) {
    return routes.reviewPage;
  }
  return internalTransferState.transactionType === TransactionTypesEnum.NEW_ACCOUNT
    ? routes.personalInfo
    : routes.review;
};

export const getInternalTransferPersonalInfoNextPage = (): string =>
  'secure/internal-transfer/contact-info';

export const getInternalTransferContactInfoNextPage = (itMultiReceivingToggle: boolean): string => {
  if (itMultiReceivingToggle) {
    return 'secure/internal-transfer/employment';
  }
  return 'secure/internal-transfer/settlement-fund';
};

export const getInternalTransferSettlementFundNextPage = (
  isSubsequentOAAccount: boolean,
  itMultiReceivingToggle: boolean,
): string => {
  if (itMultiReceivingToggle) {
    return 'secure/internal-transfer/new-account-review';
  }
  return isSubsequentOAAccount
    ? 'secure/internal-transfer/investment-objectives'
    : 'secure/internal-transfer/employment';
};

export const getInternalTransferEmploymentNextPage = (
  employment: Partial<VgEmploymentState> | undefined,
): string => {
  const isVanguardEmployee =
    employment?.employmentStatus?.employmentStatus === EmploymentStatus.VANGUARD_EMPLOYED;
  const isSpouseVanguardEmployee = employment?.associations?.spouse ?? false;
  const isAssociatedPerson = employment?.associations?.associated ?? false;
  if (isAssociatedPerson && !isVanguardEmployee && !isSpouseVanguardEmployee) {
    return 'secure/internal-transfer/upload/letter';
  }
  return getInternalTransferLetterUploadNextPage();
};

export const getInternalTransferLetterUploadNextPage = () =>
  'secure/internal-transfer/income-net-worth';

export const getInternalTransferIncomeNetWorthNextPage = (
  itMultiReceivingToggle: boolean,
  isEditingProfile: boolean,
): string => {
  if (itMultiReceivingToggle && isEditingProfile) {
    return 'secure/internal-transfer/new-account-review';
  }
  return 'secure/internal-transfer/investment-objectives';
};

export const getInternalTransferInvestObjNextPage = (itMultiReceivingToggle: boolean): string =>
  itMultiReceivingToggle
    ? 'secure/internal-transfer/settlement-fund'
    : 'secure/internal-transfer/review';

export const getInternalTransferNewAccountReviewNextPage = (): string =>
  'secure/internal-transfer/multi-amounts';

export const getInternalTransferReviewNextPage = (
  internalTransfer: InternalTransferState,
): string => {
  const isAdditionalSignerFlow: boolean = internalTransfer.transferId !== null;

  if (isAdditionalSignerFlow) {
    return `secure/internal-transfer/terms/existing-multisigner`;
  }

  if (internalTransfer.transactionType === TransactionTypesEnum.NEW_ACCOUNT) {
    return 'secure/internal-transfer/terms/new';
  }
  return 'secure/internal-transfer/terms/existing';
};

export const getInternalTransferCostBasisElectionNextPage = (): string =>
  'secure/internal-transfer/holdings-review';

export const getInternalTransferTermsExistingNextPage = (
  internalTransferState: InternalTransferState,
): string => {
  const isAdditionalSignerFlow: boolean = internalTransferState.transferId !== null;

  return isAdditionalSignerFlow
    ? `secure/internal-transfer/confirm-multisigner`
    : 'secure/internal-transfer/confirm';
};

export const getInternalTransferTermsNewNextPage = (): string => 'secure/internal-transfer/confirm';

export const getDivorceDocUploadNextPage = (itMultiReceivingToggle: boolean): string =>
  itMultiReceivingToggle ? 'secure/divorce/from-to' : 'secure/divorce/transfer-details';

export const getDivorceTransferDetailsNextPage = (): string => 'secure/divorce/holdings-review';

export const getDivorceReviewHoldingsNextPage = (): string => 'secure/divorce/review';

export const getDivorceReviewNextPage = (): string => 'secure/divorce/terms/divorce-existing';

export const getDivorceTermsNextPage = (): string => 'secure/divorce/confirm';

// Divorce multi-IT flow helpers
export const getDivorceFromToNextPage = (): string => 'secure/divorce/amounts';

export const getDivorceAmountsNextPage = (
  hasAvgCostHoldings: boolean,
  hasMinTaxHoldings: boolean,
  isManaged: boolean,
): string => {
  if (hasAvgCostHoldings || (isManaged && hasMinTaxHoldings)) {
    return 'secure/divorce/cost-basis-election';
  }
  return 'secure/divorce/multi-review';
};

export const getDivorceCostBasisElectionNextPage = (): string => 'secure/divorce/multi-review';

export const getDivorceMultiReviewNextPage = (): string => 'secure/divorce/terms/divorce-existing';

// Multi-receiving flow helpers
export const getInternalTransferFromToNextPage = (
  hasNewAccounts: boolean,
  itMultiReceivingToggle: boolean,
): string =>
  itMultiReceivingToggle && hasNewAccounts
    ? 'secure/internal-transfer/personal-info'
    : 'secure/internal-transfer/multi-amounts';

export const getInternalTransferAmountsNextPage = (
  hasAvgCostHoldings: boolean,
  hasMinTaxHoldings: boolean,
  isManaged: boolean,
): string => {
  if (hasAvgCostHoldings || (isManaged && hasMinTaxHoldings)) {
    return 'secure/internal-transfer/multi-cost-basis-election';
  }
  return 'secure/internal-transfer/multi-review';
};

export const getInternalTransferCostBasisElectionMultiNextPage = (): string =>
  'secure/internal-transfer/multi-review';

export const getInternalTransferMultiReviewNextPage = (hasNewAccount = false): string =>
  hasNewAccount ? 'secure/internal-transfer/terms/new' : 'secure/internal-transfer/terms/existing';
